import { LocationItem, SearchData } from '../../../types/search';

interface CategoryItem {
  category_ID: string;
  category_Name_TH: string;
  category_Name_EN: string;
  category_Name_CN: string;
}

interface ActionItem {
  id: string;
  action: string;
}

export class UrlService {
  static generateSearchUrl(
    searchData: SearchData,
    filteredActions: ActionItem[],
    filteredCategories: CategoryItem[],
    locationTypeId: number | null,
    locale: string,
    t: (key: string) => string
  ): string {
    const { 
      transactionType, 
      propertyType, 
      searchText, 
      priceMin, 
      priceMax, 
      location: selectedLocation, 
      subLocation: selectedSubLocation,
      selectedBedrooms,
      selectedBathrooms,
      selectedQuota,
      selectedStatus,
      selectedFloor,
      selectedView,
      selectedDirection,
      selectedPublishedBy,
      selectedPostedDate,
      areaMin,
      areaMax,
      selectedDynamicFilters,
      apiQueryParams
    } = searchData;
    
    const urlParts: string[] = [];
    const queryParams: string[] = [];

    if (transactionType) {
      const actionObj = filteredActions.find(action => action.action === transactionType);
      
      if (actionObj) {
        urlParts.push(actionObj.action);
      } else {
        urlParts.push(transactionType);
      }
    } else {
      urlParts.push(t('estate'));
    }

    if (propertyType) {
      const categoryObj = filteredCategories.find(category => {
        if (locale === 'en') return category.category_Name_EN === propertyType;
        if (locale === 'cn') return category.category_Name_CN === propertyType;
        return category.category_Name_TH === propertyType;
      });
      
      if (categoryObj) {
        if (locale === 'en') {
          urlParts.push(categoryObj.category_Name_EN);
        } else if (locale === 'cn') {
          urlParts.push(categoryObj.category_Name_CN);
        } else {
          urlParts.push(categoryObj.category_Name_TH);
        }
      } else if (propertyType === t('all')) {
        urlParts.push('ทั้งหมด');
      } else {
        urlParts.push(propertyType);
      }
    } else {
      urlParts.push('ทั้งหมด');
    }

    if (selectedLocation) {
      if (locationTypeId === -1) {
        urlParts.push(t('project_name'));
        urlParts.push(selectedLocation.originalLabel || selectedLocation.label);

        if (selectedLocation.value) {
          queryParams.push(`projectid=${selectedLocation.value}`);
        }
      } else {
        if (searchText && searchText.includes(' - ')) {
          const parts = searchText.split(' - ');
          
          if (parts.length === 3) {
            const [provinceName, amphurName, districtName] = parts;
            urlParts.push(provinceName.trim());
            urlParts.push(amphurName.trim());
            urlParts.push(districtName.trim());
          } else if (parts.length === 2) {
            const [firstPart, secondPart] = parts;
            urlParts.push(firstPart.trim());
            urlParts.push(secondPart.trim());
          } else {
            urlParts.push(selectedLocation.label);
            
            if (selectedSubLocation &&
              selectedSubLocation.value &&
              !selectedSubLocation.value.toString().includes('all_')) {
              urlParts.push(selectedSubLocation.label);
            }
          }
        } else {
          urlParts.push(selectedLocation.label);

          if (selectedSubLocation &&
            selectedSubLocation.value &&
            !selectedSubLocation.value.toString().includes('all_')) {
            urlParts.push(selectedSubLocation.label);
          }
        }
      }
    }

    if (priceMin && priceMin > 0) {
      queryParams.push(`startPrice=${priceMin}`);
    }

    if (priceMax && priceMax < 10000000) {
      queryParams.push(`endPrice=${priceMax}`);
    }

    if (areaMin && areaMin > 0) {
      queryParams.push(`areaMin=${areaMin}`);
    }

    if (areaMax && areaMax < 1000) {
      queryParams.push(`areaMax=${areaMax}`);
    }

    if (selectedBedrooms) {
      queryParams.push(`bedrooms=${encodeURIComponent(selectedBedrooms)}`);
    }

    if (selectedBathrooms) {
      queryParams.push(`bathrooms=${encodeURIComponent(selectedBathrooms)}`);
    }

    if (selectedQuota) {
      queryParams.push(`quota=${encodeURIComponent(selectedQuota)}`);
    }

    if (selectedStatus) {
      queryParams.push(`status=${encodeURIComponent(selectedStatus)}`);
    }

    if (selectedFloor) {
      queryParams.push(`floor=${encodeURIComponent(selectedFloor)}`);
    }

    if (selectedView) {
      queryParams.push(`view=${encodeURIComponent(selectedView)}`);
    }

    if (selectedDirection) {
      queryParams.push(`direction=${encodeURIComponent(selectedDirection)}`);
    }

    if (selectedPublishedBy) {
      queryParams.push(`publishedBy=${encodeURIComponent(selectedPublishedBy)}`);
    }

    if (selectedPostedDate) {
      queryParams.push(`postedDate=${encodeURIComponent(selectedPostedDate)}`);
    }

    if (apiQueryParams && Object.keys(apiQueryParams).length > 0) {
      Object.entries(apiQueryParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
    }

    if (selectedDynamicFilters) {
      Object.entries(selectedDynamicFilters).forEach(([groupId, optionId]) => {
        const hasApiParam = apiQueryParams && Object.keys(apiQueryParams).some(key => 
          key.includes(groupId) || key.includes(optionId.toString())
        );
        
        if (!hasApiParam) {
          queryParams.push(`dynamicFilter_${groupId}=${optionId}`);
        }
      });
    }

    const url = urlParts.join('/') + (queryParams.length > 0 ? '?' + queryParams.join('&') : '');
    
    return url;
  }

  static navigateToUrl(url: string): void {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    }
  }
} 